import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {filter} from 'rxjs/operators';
import {API_URL} from '../constants';

export interface MenuColor {
    id: string;
    label: string;
    ink: string;      // the main menu / header colour
    dark: string;     // darker stop of the header + footer gradient
    light: string;    // lighter stop
    lightBg: boolean; // pale colour: text on the menu/header/footer flips to dark ink
    isGradient?: boolean; // preview as a gradient (Keel's wall-board looks)
}

export interface AccentColor {
    id: string;
    label: string;
    value: string;
    lightBg: boolean; // pale colour: text on accent buttons flips to dark ink
}

export interface TitleColor {
    id: string;
    label: string;
    value: string | null; // null = follow the normal text colour
}

export interface ThemeChoice {
    menu: string;
    accent: string;
    title: string;
}

// ---------------------------------------------------------------- colour helpers

function parseHex(hex: string): [number, number, number] {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb: number[]): string {
    return '#' + rgb.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

/** Blends {@code hex} toward {@code toward} by {@code amount} (0..1). */
export function mix(hex: string, toward: string, amount: number): string {
    const a = parseHex(hex);
    const b = parseHex(toward);
    return toHex(a.map((v, i) => v + (b[i] - v) * amount));
}

function solid(id: string, label: string, ink: string, lightBg = false): MenuColor {
    return lightBg
        ? {id, label, ink, dark: mix(ink, '#000000', 0.07), light: mix(ink, '#ffffff', 0.55), lightBg}
        : {id, label, ink, dark: mix(ink, '#000000', 0.28), light: mix(ink, '#ffffff', 0.1), lightBg};
}

function gradient(id: string, label: string, from: string, to: string): MenuColor {
    return {id, label, ink: mix(from, to, 0.5), dark: from, light: to, lightBg: false, isGradient: true};
}

// ---------------------------------------------------------------- palettes

/**
 * Menu & header colours: Rana's own deep tones first, then every sidebar background and wall-board gradient used
 * by the Keel project, so both products offer the same set of looks.
 */
export const MENU_COLORS: MenuColor[] = [
    // Rana
    {id: 'maroon', label: 'Royal Maroon', ink: '#550000', dark: '#220509', light: '#3a0b16', lightBg: false},
    {id: 'navy', label: 'Midnight Navy', ink: '#0f1c3a', dark: '#09122a', light: '#18294f', lightBg: false},
    {id: 'forest-green', label: 'Forest Green', ink: '#0f2e1f', dark: '#0a2016', light: '#17402c', lightBg: false},
    {id: 'royal-purple', label: 'Royal Purple', ink: '#2a1245', dark: '#1d0b32', light: '#3a1b5e', lightBg: false},
    {id: 'charcoal', label: 'Charcoal', ink: '#1f2229', dark: '#15171c', light: '#2c3038', lightBg: false},
    solid('gold', 'Gold', '#44370c'),
    solid('black', 'Black', '#191919'),
    solid('red', 'Red', '#d50000'),
    solid('blue', 'Blue', '#2196f3'),
    solid('indigo', 'Indigo', '#3f51b5'),
    solid('pink', 'Pink', '#a31545'),
    solid('orange', 'Orange', '#ad3f00'),
    solid('dark-grey', 'Dark Grey', '#343a40'),
    solid('light-grey', 'Light Grey', '#e0e0e0', true)
   
];

/** Highlight colours: Rana's saffron plus the twelve accent colours of the Keel sidebar filter. */
export const ACCENT_COLORS: AccentColor[] = [
    {id: 'saffron', label: 'Saffron', value: 'rgb(255, 106, 0)', lightBg: false},
    {id: 'gold', label: 'Gold', value: '#9a8e67', lightBg: false},
    {id: 'royal-blue', label: 'Royal Blue', value: '#2f6fed', lightBg: false},
    {id: 'crimson', label: 'Crimson', value: '#c0392b', lightBg: false},
    // Keel
    {id: 'purple', label: 'Purple', value: '#8e00ab', lightBg: false},
    {id: 'azure', label: 'Azure', value: '#00bcd4', lightBg: false},
    {id: 'green', label: 'Green', value: '#43a047', lightBg: false},
    {id: 'orange', label: 'Orange', value: '#ff9800', lightBg: false},
    {id: 'red', label: 'Red', value: '#ff1744', lightBg: false},
    {id: 'blue', label: 'Blue', value: '#2196f3', lightBg: false},
    {id: 'pink', label: 'Pink', value: '#ec407a', lightBg: false},
    {id: 'lime', label: 'Lime', value: '#cddc39', lightBg: false},
    {id: 'dark-grey', label: 'Dark Grey', value: '#343a40', lightBg: false},
    {id: 'light-grey', label: 'Light Grey', value: '#bdbdbd', lightBg: true}
];

export const TITLE_COLORS: TitleColor[] = [
    {id: 'default', label: 'Default', value: null},
    {id: 'maroon', label: 'Maroon', value: '#6b0f1a'},
    {id: 'navy', label: 'Navy', value: '#12224a'},
    {id: 'accent', label: 'Accent', value: 'var(--primary)'},
    {id: 'black', label: 'Black', value: '#111111'}
];

export const DEFAULT_THEME: ThemeChoice = {menu: 'maroon', accent: 'saffron', title: 'default'};

const STORAGE_PREFIX = 'rana.theme.';

/**
 * The look of the site that a visitor can change with the gear icon: menu/header colour, highlight colour and
 * page-title colour. It only sets CSS variables on <html>, so every page follows without per-component code.
 *
 * Signed-in members only: their choice is kept server-side (UserPreference table) so it follows them to any browser,
 * with a per-user browser cache for instant paint. Signed-out visitors always see the default look and no gear.
 * The cache is keyed by user, so a shared computer never shows one person's colours to the next.
 */
@Injectable({providedIn: 'root'})
export class ThemeService {

    private readonly subject = new BehaviorSubject<ThemeChoice>({...DEFAULT_THEME});
    readonly theme$ = this.subject.asObservable();
    private activeUser: string | null | undefined = undefined; // undefined = not resolved yet

    /** The colour settings are a signed-in feature: nobody who is signed out sees the gear or a customised look. */
    get signedIn(): boolean {
        return !!this.activeUser;
    }

    constructor(private http: HttpClient, router: Router) {
        this.syncUser();
        // Sign-in / sign-out happen through navigation, so this is where a change of user is noticed.
        router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.syncUser());
    }

    get current(): ThemeChoice {
        return this.subject.value;
    }

    set(change: Partial<ThemeChoice>): void {
        if (!this.activeUser) return; // signed out: the default look only
        const next = this.sanitize({...this.subject.value, ...change});
        this.publish(next);
        this.cache(next);
        this.saveToServer(next);
    }

    reset(): void {
        if (!this.activeUser) return;
        this.publish({...DEFAULT_THEME});
        try {
            localStorage.removeItem(this.storageKey(this.activeUser));
        } catch {
            // storage unavailable (private window): the reset still applies for this page view
        }
        this.saveToServer({...DEFAULT_THEME});
    }

    // ---------------------------------------------------------------- user handling

    private currentUser(): string | null {
        try {
            return sessionStorage.getItem('userId') || null;
        } catch {
            return null;
        }
    }

    private syncUser(): void {
        const user = this.currentUser();
        if (user === this.activeUser) return;
        this.activeUser = user;

        // Signed out: always the default look. Signed in: this member's own cached look (or the default) — never the
        // previous user's.
        this.publish(user ? this.readCache(user) : {...DEFAULT_THEME});

        if (user) {
            this.http.get<{ themeMenu?: string; themeAccent?: string; themeTitle?: string }>(`${API_URL}preferences/me`).subscribe({
                next: (pref) => {
                    if (this.activeUser !== user) return; // another user signed in meanwhile
                    const hasServerChoice = !!(pref?.themeMenu || pref?.themeAccent || pref?.themeTitle);
                    if (!hasServerChoice) return;
                    const fromServer = this.sanitize({
                        menu: pref.themeMenu || DEFAULT_THEME.menu,
                        accent: pref.themeAccent || DEFAULT_THEME.accent,
                        title: pref.themeTitle || DEFAULT_THEME.title
                    });
                    this.publish(fromServer);
                    this.cache(fromServer);
                },
                error: () => { /* preferences service unavailable: the cached look is fine */ }
            });
        }
    }

    private saveToServer(theme: ThemeChoice): void {
        if (!this.activeUser) return; // not signed in: browser-local only
        this.http.put(`${API_URL}preferences/me`, {
            themeMenu: theme.menu === DEFAULT_THEME.menu ? null : theme.menu,
            themeAccent: theme.accent === DEFAULT_THEME.accent ? null : theme.accent,
            themeTitle: theme.title === DEFAULT_THEME.title ? null : theme.title
        }).subscribe({error: () => { /* kept in the browser cache; will be saved on the next change */ }});
    }

    // ---------------------------------------------------------------- applying + storage

    private publish(theme: ThemeChoice): void {
        this.subject.next(theme);
        this.apply(theme);
    }

    private apply(theme: ThemeChoice): void {
        const html = document.documentElement;
        const root = html.style;
        const menu = MENU_COLORS.find(m => m.id === theme.menu) || MENU_COLORS[0];
        const accent = ACCENT_COLORS.find(a => a.id === theme.accent) || ACCENT_COLORS[0];
        const title = TITLE_COLORS.find(t => t.id === theme.title) || TITLE_COLORS[0];

        root.setProperty('--rana-nav-ink', menu.ink);
        root.setProperty('--rana-nav-ink-dark', menu.dark);
        root.setProperty('--rana-nav-ink-light', menu.light);
        root.setProperty('--primary', accent.value);
        root.setProperty('--rana-on-primary', accent.lightBg ? '#212121' : '#ffffff');
        if (title.value) {
            root.setProperty('--rana-title-color', title.value);
        } else {
            root.removeProperty('--rana-title-color');
        }
        // Flags the stylesheet reads to flip text to dark ink on pale colours.
        this.flag(html, 'navLight', menu.lightBg);
        this.flag(html, 'accentLight', accent.lightBg);
        this.flag(html, 'titleCustom', !!title.value);
    }

    private flag(html: HTMLElement, name: string, on: boolean): void {
        if (on) html.dataset[name] = 'true';
        else delete html.dataset[name];
    }

    private sanitize(theme: ThemeChoice): ThemeChoice {
        return {
            menu: MENU_COLORS.some(m => m.id === theme.menu) ? theme.menu : DEFAULT_THEME.menu,
            accent: ACCENT_COLORS.some(a => a.id === theme.accent) ? theme.accent : DEFAULT_THEME.accent,
            title: TITLE_COLORS.some(t => t.id === theme.title) ? theme.title : DEFAULT_THEME.title
        };
    }

    private storageKey(user: string): string {
        return STORAGE_PREFIX + user;
    }

    private readCache(user: string): ThemeChoice {
        try {
            const raw = localStorage.getItem(this.storageKey(user));
            if (raw) return this.sanitize({...DEFAULT_THEME, ...JSON.parse(raw)});
        } catch {
            // unreadable or blocked storage: use the default look
        }
        return {...DEFAULT_THEME};
    }

    private cache(theme: ThemeChoice): void {
        if (!this.activeUser) return;
        try {
            localStorage.setItem(this.storageKey(this.activeUser), JSON.stringify(theme));
        } catch {
            // not persisted; still applied for this page view
        }
    }
}
